import React from 'react';
import { MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { budgetCountries } from '../../../data/budget-countries';
import { MEDIA_TAGS } from '../../../data/media-tags';

interface PostMetadataProps {
  username: string;
  caption: string;
  city?: string;
  country?: string;
  tags: string[];
  createdAt: Date;
}

export function PostMetadata({
  username,
  caption,
  city,
  country,
  tags,
  createdAt
}: PostMetadataProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const MAX_CAPTION_LENGTH = 150;
  const shouldTruncate = caption.length > MAX_CAPTION_LENGTH;
  
  const displayedCaption = shouldTruncate && !isExpanded 
    ? `${caption.substring(0, MAX_CAPTION_LENGTH)}...` 
    : caption;

  return (
    <>
      <div className="mb-2">
        <span className="font-semibold text-gray-900 dark:text-white text-sm">
          {username}
        </span>
        <span className="ml-1.5 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
          {displayedCaption}
        </span>
        {shouldTruncate && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="ml-1 text-gray-400 dark:text-gray-500 text-xs font-medium hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            {isExpanded ? 'moins' : 'plus'}
          </button>
        )}
      </div>

      {(city || country) && (
        <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 dark:text-gray-400">
          <MapPin className="w-3.5 h-3.5" />
          <span>
            {city}
            {city && country && ', '}
            {budgetCountries.find(c => c.code === country)?.name || country}
          </span>
        </div>
      )}

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2 mt-2">
          {tags.map(tag => (
            <Link
              to={`/media?tag=${tag}`}
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-400 rounded-md text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-xs">{MEDIA_TAGS[tag]?.icon || '#'}</span>
              <span>{MEDIA_TAGS[tag]?.label || tag}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
        {formatDistanceToNow(createdAt, { addSuffix: true, locale: fr }).replace('environ ', '')}
      </div>
    </>
  );
}